/**
 * Highlight Context for Dual-View Synchronization
 *
 * Manages cross-highlighting state between the Value Flows canvas and
 * the algebra/observables inspectors. Implements graph_binding-based
 * synchronization as specified in the design doc.
 *
 * @see docs/designs/smart-asset-algebra-visualization.md
 * @see docs/discoveries/2026-01-05-ui-schema-consumption.md
 */
import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from "react";

/**
 * Highlight source identifies where the highlight originated
 */
export type HighlightSource = "graph" | "algebra" | "observable" | "temporal";

/**
 * Highlight state for cross-view synchronization
 */
export interface HighlightState {
  /** Currently highlighted node IDs */
  nodeIds: string[];
  /** Currently highlighted expression IDs */
  expressionIds: string[];
  /** Currently highlighted observable IDs */
  observableIds: string[];
  /** Highlight group (for multi-selection) */
  highlightGroup?: string;
  /** Source of the highlight */
  source?: HighlightSource;
}

/**
 * Highlight context value
 */
interface HighlightContextValue {
  /** Current highlight state */
  highlight: HighlightState;
  /** Highlight nodes (from graph click) */
  highlightNodes: (nodeIds: string[], highlightGroup?: string) => void;
  /** Highlight expressions (from algebra click) */
  highlightExpressions: (
    expressionIds: string[],
    nodeIds?: string[],
    highlightGroup?: string,
  ) => void;
  /** Highlight observables (from observable click) */
  highlightObservables: (observableIds: string[], nodeIds?: string[]) => void;
  /** Highlight from temporal lane click */
  highlightFromTemporal: (expressionId: string, nodeId?: string, highlightGroup?: string) => void;
  /** Clear all highlights */
  clearHighlight: () => void;
  /** Check if a node is highlighted */
  isNodeHighlighted: (nodeId: string) => boolean;
  /** Check if an expression is highlighted */
  isExpressionHighlighted: (expressionId: string) => boolean;
  /** Check if an observable is highlighted */
  isObservableHighlighted: (observableId: string) => boolean;
}

const initialState: HighlightState = {
  nodeIds: [],
  expressionIds: [],
  observableIds: [],
};

const HighlightContext = createContext<HighlightContextValue | null>(null);

/**
 * Hook to access highlight context
 */
export function useHighlight(): HighlightContextValue {
  const context = useContext(HighlightContext);
  if (!context) {
    throw new Error("useHighlight must be used within a HighlightProvider");
  }
  return context;
}

/**
 * Optional hook that returns null if not within provider
 */
export function useHighlightOptional(): HighlightContextValue | null {
  return useContext(HighlightContext);
}

interface HighlightProviderProps {
  children: ReactNode;
}

/**
 * Highlight Provider Component
 *
 * Manages cross-highlighting state for dual-view synchronization
 */
export function HighlightProvider({ children }: HighlightProviderProps) {
  const [highlight, setHighlight] = useState<HighlightState>(initialState);

  const highlightNodes = useCallback((nodeIds: string[], highlightGroup?: string) => {
    setHighlight({
      nodeIds,
      expressionIds: [],
      observableIds: [],
      highlightGroup,
      source: "graph",
    });
  }, []);

  const highlightExpressions = useCallback(
    (expressionIds: string[], nodeIds: string[] = [], highlightGroup?: string) => {
      setHighlight({
        nodeIds,
        expressionIds,
        observableIds: [],
        highlightGroup,
        source: "algebra",
      });
    },
    [],
  );

  const highlightObservables = useCallback((observableIds: string[], nodeIds: string[] = []) => {
    setHighlight({
      nodeIds,
      expressionIds: [],
      observableIds,
      source: "observable",
    });
  }, []);

  const highlightFromTemporal = useCallback(
    (expressionId: string, nodeId?: string, highlightGroup?: string) => {
      setHighlight({
        nodeIds: nodeId ? [nodeId] : [],
        expressionIds: [expressionId],
        observableIds: [],
        highlightGroup,
        source: "temporal",
      });
    },
    [],
  );

  const clearHighlight = useCallback(() => {
    setHighlight(initialState);
  }, []);

  const isNodeHighlighted = useCallback(
    (nodeId: string) => {
      return highlight.nodeIds.includes(nodeId);
    },
    [highlight.nodeIds],
  );

  const isExpressionHighlighted = useCallback(
    (expressionId: string) => {
      return highlight.expressionIds.includes(expressionId);
    },
    [highlight.expressionIds],
  );

  const isObservableHighlighted = useCallback(
    (observableId: string) => {
      return highlight.observableIds.includes(observableId);
    },
    [highlight.observableIds],
  );

  const value = useMemo(
    () => ({
      highlight,
      highlightNodes,
      highlightExpressions,
      highlightObservables,
      highlightFromTemporal,
      clearHighlight,
      isNodeHighlighted,
      isExpressionHighlighted,
      isObservableHighlighted,
    }),
    [
      highlight,
      highlightNodes,
      highlightExpressions,
      highlightObservables,
      highlightFromTemporal,
      clearHighlight,
      isNodeHighlighted,
      isExpressionHighlighted,
      isObservableHighlighted,
    ],
  );

  return <HighlightContext.Provider value={value}>{children}</HighlightContext.Provider>;
}
